import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader/PageHeader";
import { ErrorPage } from "@/components/ErrorPage/ErrorPage";
import { ErrorPageType, PermissionRoles } from "@/enum";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useHasPermissions } from "@/utils/useHasPermissions";

import { AdoptionTab } from "./components/AdoptionTab";
import { UnusedFeaturesTab } from "./components/UnusedFeaturesTab";
import { CompanyViewTab } from "./components/CompanyViewTab";
import { AllCustomersTab } from "./components/AllCustomersTab";

const tabs = ["adoption", "unused", "company", "all"] as const;
export function FeatureUsage() {
	const { t } = useTranslation();
	const [params, setParams] = useSearchParams();
	const { user } = useAppSelector(authSelector);
	const { hasPermissions } = useHasPermissions();

	// FeatureRoute's shared company gate waits for a truthy id. Do not mount any usage query
	// while the company is unresolved, including when this page is rendered independently.
	if (!user?.company?.id) {
		return <div role="status">{t("featureUsage.loading")}</div>;
	}

	if (user.company.id !== 1 || !hasPermissions([PermissionRoles.GLOBAL_ADMIN])) {
		return <ErrorPage type={ErrorPageType.Forbidden} />;
	}

	const requested = params.get("tab");
	const tab = tabs.find((value) => value === requested) ?? "adoption";

	return (
		<div className="w-full">
			<PageHeader
				title={t("featureUsage.title")}
				activeTab={tab}
				tabs={tabs.map((value) => ({ value, label: t(`featureUsage.tabs.${value}`) }))}
				onTabChange={(value) => {
					const next = new URLSearchParams(params);

					next.set("tab", value);
					setParams(next, { replace: true });
				}}
			/>
			{tab === "adoption" && <AdoptionTab />}
			{tab === "unused" && <UnusedFeaturesTab />}
			{tab === "company" && <CompanyViewTab />}
			{tab === "all" && <AllCustomersTab />}
		</div>
	);
}
